import { useEffect, useRef, useState } from "react";
import { redirect, useActionData, useLoaderData, useNavigation, useSubmit, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { RepositorySnapshot } from "@edm/contracts";
import { Loading, SettingsRowsSkeleton } from "../components/skeletons.jsx";
import { Deferred } from "../components/states.jsx";
import { useI18n } from "../i18n/index.js";
import { isApiError, type ConnectorSummary, type SourceSync, type WorkspaceApi } from "../lib/api.js";
import { deferred } from "../lib/deferred.js";
import { apiContext, invalidateSession, sessionContext } from "../lib/session.js";
import { workspaceContext } from "../lib/workspace.js";
import { Badge, Button, Callout, CenteredPage, CopyIcon, SettingsBlock, SettingsRow, SettingsSection, Skeleton, Tabs, UploadIcon, useToast } from "../ui/index.js";
import { ConnectorLogo, useInstallOutcome } from "./workspace/connectors.jsx";

/**
 * Setting a workspace up (D-0022): connect the places its decisions already live, or upload files, and
 * Diotia reads them once. With nothing chosen the only way on is to skip. Until the read is over the
 * workspace's pages say so (`components/setup-gate.tsx`); what it finds waits in Drafts to be confirmed.
 * Or the person hands the reading to their own coding agent: it connects over MCP (D-0021), reads the
 * organization's repositories, and proposes what it finds, which waits in Reviews.
 */

type UploadedFile = { path: string; content: string };
type ActionResult = { error?: string };

// What core-engine reads from an upload: Markdown and plain text, each within its per-file limit.
const ACCEPT = ".md,.markdown,.mdx,.txt,.text,.rst,.adoc,.asciidoc,.org";
const MAX_FILE_BYTES = 500_000;
const MAX_FILES = 1000;

// Picked files outlive the round trip to Notion or Atlassian that connecting takes. Best effort: too
// large for the session store, they stay in memory and a round trip drops them.
const storageKey = (workspaceId: string) => `edm.setupFiles.${workspaceId}`;
function readStored(workspaceId: string): UploadedFile[] {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey(workspaceId)) ?? "[]") as UploadedFile[];
  } catch {
    return [];
  }
}
function store(workspaceId: string, files: UploadedFile[]) {
  try {
    if (files.length) sessionStorage.setItem(storageKey(workspaceId), JSON.stringify(files));
    else sessionStorage.removeItem(storageKey(workspaceId));
  } catch {
    // over the quota, or storage is off
  }
}

function readText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function upload(files: UploadedFile[]): RepositorySnapshot {
  const now = new Date().toISOString();
  return {
    repository: { uri: "upload:files", system: "upload" },
    revision: `upload-${now}`,
    capturedAt: now,
    tree: { paths: files.map((f) => f.path) },
    documents: files.map((f) => ({ path: f.path, content: f.content })),
  };
}

export function setupLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  // Set up already, or being read: the workspace is where to be.
  if (!ws.setup || ws.setup === "running") throw redirect(`/w/${ws.slug}`);
  const me = context.get(sessionContext);
  const api = context.get(apiContext).workspace(ws.id);
  const data = Promise.all([api.listConnectors(), api.get(), api.setup(), api.listAgentConnections()]).then(([page, detail, setup, agents]) => {
    const role = detail.members.find((m) => m.userId === me?.actor.id)?.role;
    return {
      sources: page.items.filter((c) => c.readsPages),
      canManage: role === "owner" || role === "admin",
      // A setup that read nothing comes back here with what went wrong.
      failed: setup.syncs.filter((s) => s.status === "failed"),
      mcpUrl: agents.mcpUrl,
    };
  });
  return { workspace: ws, api, data: deferred(data) };
}

export async function setupAction({ context, request }: ActionFunctionArgs): Promise<ActionResult | Response> {
  const ws = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(ws.id);
  const input = (await request.json()) as { intent: "start" | "skip" | "agent"; sources?: string[]; files?: UploadedFile[] };
  try {
    if (input.intent === "skip") await api.skipSetup();
    else if (input.intent === "agent") await api.delegateSetup();
    else await api.startSetup({ sources: input.sources ?? [], ...(input.files?.length ? { upload: upload(input.files) } : {}) });
  } catch (err) {
    // Whatever went wrong, the person keeps the page and what they chose on it.
    if (isApiError(err) && err.code !== "unauthorized") return { error: err.message };
    throw err;
  }
  store(ws.id, []);
  // The session says how far setup is; the workspace's pages read it from there.
  invalidateSession();
  return redirect(`/w/${ws.slug}`);
}

function Head() {
  const { t } = useI18n();
  return (
    <div className="dt-centered__head">
      <h1>{t("setup.title")}</h1>
      <p className="dt-muted">{t("setup.subtitle")}</p>
    </div>
  );
}

export function SetupSkeleton() {
  return (
    <CenteredPage width={520}>
      <Head />
      <SetupBodySkeleton />
    </CenteredPage>
  );
}

function SetupBodySkeleton() {
  return (
    <Loading>
      <SettingsSection>
        <SettingsRowsSkeleton count={3} description={false} control={<Skeleton shape="button-sm" width={72} />} />
      </SettingsSection>
      <div className="dt-form__actions">
        <Skeleton shape="button" width={88} />
      </div>
    </Loading>
  );
}

export function SetupRoute() {
  const { workspace, api, data } = useLoaderData<typeof setupLoader>();
  return (
    <CenteredPage width={520}>
      <Head />
      <Deferred data={data} fallback={<SetupBodySkeleton />}>
        {(d) => <Setup {...d} workspace={workspace} setupPath={`/w/${workspace.slug}/setup`} api={api} />}
      </Deferred>
    </CenteredPage>
  );
}

/** A failed read in a word the page knows, or the source's own message. */
export function useSyncError() {
  const { tOr } = useI18n();
  return (error: string | null) => tOr(`setup.error.${error ?? ""}`, error ?? "");
}

function Setup({ sources, canManage, failed, mcpUrl, workspace, setupPath, api }: { sources: ConnectorSummary[]; canManage: boolean; failed: SourceSync[]; mcpUrl: string; workspace: { id: string; name: string }; setupPath: string; api: WorkspaceApi }) {
  const workspaceId = workspace.id;
  const { t, tPlural } = useI18n();
  const toast = useToast();
  const submit = useSubmit();
  const navigation = useNavigation();
  const result = useActionData() as ActionResult | undefined;
  const syncError = useSyncError();
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<UploadedFile[]>(() => readStored(workspaceId));
  const [leaving, setLeaving] = useState<string | null>(null);
  useInstallOutcome(sources);

  useEffect(() => store(workspaceId, files), [workspaceId, files]);
  const told = useRef<ActionResult>(undefined);
  useEffect(() => {
    if (!result?.error || told.current === result) return;
    told.current = result;
    toast.danger(result.error);
  }, [result, toast]);

  const connected = sources.filter((s) => s.installation);
  const ready = connected.length > 0 || files.length > 0;
  const sending = navigation.state !== "idle";

  const connect = async (source: ConnectorSummary) => {
    setLeaving(source.provider);
    try {
      window.location.assign((await api.installConnector(source.provider, setupPath)).url);
    } catch (err) {
      setLeaving(null);
      toast.danger(t("connectors.installFailed", { name: source.name }), err instanceof Error ? err.message : String(err));
    }
  };

  const pick = async (list: FileList | null) => {
    const picked = Array.from(list ?? []);
    const tooLarge = picked.filter((f) => f.size > MAX_FILE_BYTES);
    for (const f of tooLarge) toast.warning(t("setup.upload.tooLarge", { name: f.name }));
    const read = await Promise.all(picked.filter((f) => f.size <= MAX_FILE_BYTES).map(async (f) => ({ path: f.name, content: await readText(f) })));
    setFiles((current) => {
      // A file picked again replaces the earlier one of the same name.
      const byPath = new Map([...current, ...read].map((f) => [f.path, f]));
      return [...byPath.values()].slice(0, MAX_FILES);
    });
    if (input.current) input.current.value = "";
  };

  const send = (intent: "start" | "skip" | "agent") => submit(intent === "start" ? { intent, sources: connected.map((s) => s.provider), files } : { intent }, { method: "post", encType: "application/json" });

  const prompt = t("setup.agent.prompt", { workspace: workspace.name, url: mcpUrl });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success(t("setup.agent.copied"));
    } catch {
      toast.danger(t("setup.agent.copyFailed"));
    }
  };

  return (
    <>
      {!canManage && <Callout tone="info">{t("setup.readOnly")}</Callout>}
      {failed.length > 0 && (
        <Callout tone="warning" title={t("setup.failed")}>
          {failed.map((f) => (
            <div key={f.id}>
              {f.source === "upload" ? t("setup.upload") : (f.sources.length ? f.sources : [f.source]).map((id) => sources.find((s) => s.provider === id)?.name ?? id).join(", ")}: {syncError(f.error)}
            </div>
          ))}
        </Callout>
      )}
      <Tabs
        items={[
          {
            value: "manual",
            label: t("setup.tab.manual"),
            content: (
              <>
                <SettingsSection>
                  {sources.map((s) => (
                    <SettingsRow
                      key={s.provider}
                      data-testid={`setup-${s.provider}`}
                      label={
                        <span className="dt-inline dt-gap-2 dt-align-center">
                          <ConnectorLogo connector={s} size={28} />
                          {s.name}
                        </span>
                      }
                      description={s.installation ? s.installation.externalTeamName || undefined : !s.configured ? t("connectors.notConfigured") : undefined}
                    >
                      {s.installation ? (
                        <Badge tone="success">{t("setup.connected")}</Badge>
                      ) : (
                        <Button size="sm" variant="secondary" disabled={!s.configured || !canManage || leaving !== null} loading={leaving === s.provider} onClick={() => void connect(s)}>
                          {t("setup.connect")}
                        </Button>
                      )}
                    </SettingsRow>
                  ))}
                  <SettingsRow
                    data-testid="setup-upload"
                    label={
                      <span className="dt-inline dt-gap-2 dt-align-center">
                        <span className="dt-connector-logo" style={{ width: 28, height: 28 }}>
                          <UploadIcon size={15} />
                        </span>
                        {t("setup.upload")}
                      </span>
                    }
                    description={files.length ? `${tPlural("setup.upload.count", files.length)}: ${files.map((f) => f.path).join(", ")}` : t("setup.upload.hint")}
                  >
                    <span className="dt-inline dt-gap-2">
                      {files.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setFiles([])}>
                          {t("setup.upload.clear")}
                        </Button>
                      )}
                      <input ref={input} type="file" multiple accept={ACCEPT} hidden aria-label={t("setup.upload")} onChange={(e) => void pick(e.currentTarget.files)} />
                      <Button size="sm" variant="secondary" disabled={!canManage} onClick={() => input.current?.click()}>
                        {t("setup.upload.choose")}
                      </Button>
                    </span>
                  </SettingsRow>
                </SettingsSection>
                {canManage && (
                  <div className="dt-form__actions">
                    {ready ? (
                      <Button variant="primary" loading={sending} onClick={() => send("start")}>
                        {t("setup.next")}
                      </Button>
                    ) : (
                      <Button variant="secondary" loading={sending} onClick={() => send("skip")}>
                        {t("setup.skip")}
                      </Button>
                    )}
                  </div>
                )}
              </>
            ),
          },
          {
            value: "agent",
            label: t("setup.tab.agent"),
            content: (
              <>
                <SettingsSection
                  description={t("setup.agent.hint")}
                  actions={
                    <Button size="sm" variant="secondary" icon={<CopyIcon />} onClick={() => void copy()}>
                      {t("common.copy")}
                    </Button>
                  }
                >
                  <SettingsBlock>
                    <pre className="dt-mono" data-testid="setup-agent-prompt" style={{ whiteSpace: "pre-wrap", margin: 0, maxHeight: 320, overflowY: "auto" }}>
                      {prompt}
                    </pre>
                  </SettingsBlock>
                </SettingsSection>
                {canManage && (
                  <div className="dt-form__actions">
                    <Button variant="primary" loading={sending} onClick={() => send("agent")}>
                      {t("setup.agent.done")}
                    </Button>
                  </div>
                )}
              </>
            ),
          },
        ]}
      />
    </>
  );
}
